import { SidebarButtons, SidebarElement } from '@/components/everridge/sidebar/types';
import AnalyticsIcon from '@/components/icons/AnalyticsIcon';
import CalendarIcon from '@/components/icons/CalendarIcon';
import CloseSidebarIcon from '@/components/icons/CloseSidebarIcon';
import DashboardSquareIcon from '@/components/icons/DashboardSquareIcon';
import DiscountIcon from '@/components/icons/DiscountIcon';
import DoorIcon from '@/components/icons/DoorIcon';
import FaqIcon from '@/components/icons/FaqIcon';
import NotificationIcon from '@/components/icons/NotificationIcon';
import OwnersIcon from '@/components/icons/OwnersIcon';
import OwnInnIcon from '@/components/icons/OwnInnIcon';
import PackageIcon from '@/components/icons/PackageIcon';
import SettingsIcon from '@/components/icons/SettingsIcon';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';
import { Roles } from '@/@types/auth.types.ts';
import { OwnerPermissionsResponseDto } from '@/features/owners/types/owners-dto';
import DashboardSquareIconSolid from '@/components/icons/DashboardSquareIconSolid';
import Door_01_IconSolid from '@/components/icons/Door_01_IconSolid';
import Calendar_04_IconSolid from '@/components/icons/Calendar_04_IconSolid';
import Analytics_01_IconSolid from '@/components/icons/Analytics_01_IconSolid';
import PackageIconSolid from '@/components/icons/PackageIconSolid';
import FaqIconSolid from '@/components/icons/FaqIconSolid';
import OwnersIconSolid from '@/components/icons/OwnersIconSolid';
import NotificationIconSolid from '@/components/icons/NotificationIconSolid';
import DiscountIconSolid from '@/components/icons/DiscountIconSolid';
import SettingsIconSolid from '@/components/icons/SettingsIconSolid';
import QuestionsIcon from '@/components/icons/QuestionsIcon';
import QuestionsIconSolid from '@/components/icons/QuestionsIconSolid';
import Mail_02_Icon from '@/components/icons/Mail_02_Icon';
import Mail_02_IconSolid from '@/components/icons/Mail_02_IconSolid';
import Exchange_01_Icon from '@/components/icons/Exchange_01_Icon';

export type SidebarConfig = {
    [key in Roles]: SidebarElement;
};

export const sidebar: SidebarConfig = {
    [Roles.owner]: {
        header: {
            title: 'Header',
            items: [
                {
                    title: 'sidebar.header.items.close',
                    type: SidebarButtons.Close,
                    enabled: () => true,
                    url: '',
                    icon: CloseSidebarIcon,
                },
            ],
        },
        main: {
            title: 'sidebar.general.title',
            items: [
                {
                    title: 'sidebar.general.items.dashboard',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '/dashboard',
                    icon: DashboardSquareIcon,
                    activeIcon: DashboardSquareIconSolid,
                },
                {
                    title: 'sidebar.general.items.apartments',
                    type: SidebarButtons.Apartments,
                    enabled: () => true,
                    url: '/apartments',
                    icon: DoorIcon,
                    activeIcon: Door_01_IconSolid,
                },
                {
                    title: 'sidebar.general.items.calendar',
                    type: SidebarButtons.Calendar,
                    enabled: (permissions?: OwnerPermissionsResponseDto) =>
                        permissions?.showCalendar ?? false,
                    url: '/calendar',
                    icon: CalendarIcon,
                    activeIcon: Calendar_04_IconSolid,
                },
                {
                    title: 'sidebar.general.items.finances',
                    type: SidebarButtons.Finances,
                    enabled: (permissions?: OwnerPermissionsResponseDto) =>
                        permissions?.showFinances ?? false,
                    url: '/finances',
                    icon: AnalyticsIcon,
                    activeIcon: Analytics_01_IconSolid,
                },
                {
                    title: 'sidebar.general.items.orders',
                    type: SidebarButtons.Orders,
                    enabled: () => useMultiCurrencyStorage.getState().ordersEnabled,
                    url: '/orders',
                    icon: PackageIcon,
                    activeIcon: PackageIconSolid,
                },
            ],
        },
        settings: {
            title: 'sidebar.other.title',
            items: [
                {
                    title: 'sidebar.other.items.faqs',
                    type: SidebarButtons.Faq,
                    enabled: () => true,
                    url: '/faqs',
                    icon: FaqIcon,
                    activeIcon: FaqIconSolid,
                },
            ],
        },
        footer: {
            title: 'Footer',
            items: [
                {
                    title: 'sidebar.footer.items.logo',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '',
                    icon: OwnInnIcon,
                },
            ],
        },
    },
    [Roles.admin]: {
        header: {
            title: 'Header',
            items: [
                {
                    title: 'sidebar.header.items.close',
                    type: SidebarButtons.Close,
                    enabled: () => true,
                    url: '',
                    icon: CloseSidebarIcon,
                },
            ],
        },
        main: {
            title: 'sidebar.general.title',
            items: [
                {
                    title: 'sidebar.general.items.dashboard',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '/dashboard',
                    icon: DashboardSquareIcon,
                    activeIcon: DashboardSquareIconSolid,
                },
                {
                    title: 'sidebar.general.items.apartments',
                    type: SidebarButtons.Apartments,
                    enabled: () => true,
                    url: '/apartments',
                    icon: DoorIcon,
                    activeIcon: Door_01_IconSolid,
                },
                {
                    title: 'sidebar.general.items.calendar',
                    type: SidebarButtons.Calendar,
                    enabled: () => true,
                    url: '/calendar',
                    icon: CalendarIcon,
                    activeIcon: Calendar_04_IconSolid,
                },
                {
                    title: 'sidebar.general.items.finances',
                    type: SidebarButtons.Finances,
                    enabled: (permissions?: OwnerPermissionsResponseDto) =>
                        permissions?.showFinances ?? false,
                    url: '/finances',
                    icon: AnalyticsIcon,
                    activeIcon: Analytics_01_IconSolid,
                },
                {
                    title: 'sidebar.general.items.owners',
                    type: SidebarButtons.Owners,
                    enabled: () => true,
                    url: '/owners',
                    icon: OwnersIcon,
                    activeIcon: OwnersIconSolid,
                },
                {
                    title: 'sidebar.general.items.orders',
                    type: SidebarButtons.Orders,
                    enabled: () => useMultiCurrencyStorage.getState().ordersEnabled,
                    url: '/orders',
                    icon: PackageIcon,
                    activeIcon: PackageIconSolid,
                },
                {
                    title: 'sidebar.general.items.notifications',
                    type: SidebarButtons.Notifications,
                    enabled: () => true,
                    url: '/notifications',
                    icon: NotificationIcon,
                    activeIcon: NotificationIconSolid,
                },
                {
                    title: 'sidebar.general.items.newsAndOffers',
                    type: SidebarButtons.NewsOffers,
                    enabled: () => true,
                    url: '/news-and-offers',
                    icon: DiscountIcon,
                    activeIcon: DiscountIconSolid,
                },
            ],
        },
        settings: {
            title: 'sidebar.other.title',
            items: [
                {
                    title: 'sidebar.other.items.faqs',
                    type: SidebarButtons.Faq,
                    enabled: () => true,
                    url: '/faqs',
                    icon: FaqIcon,
                    activeIcon: FaqIconSolid,
                },
                {
                    title: 'sidebar.other.items.settings',
                    type: SidebarButtons.Settings,
                    enabled: () => true,
                    url: '/settings',
                    icon: SettingsIcon,
                    activeIcon: SettingsIconSolid,
                },
            ],
        },
        footer: {
            title: 'Footer',
            items: [
                {
                    title: 'sidebar.footer.items.logo',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '/',
                    icon: OwnInnIcon,
                },
            ],
        },
    },
    [Roles.superAdmin]: {
        header: {
            title: 'Header',
            items: [
                {
                    title: 'sidebar.header.items.close',
                    type: SidebarButtons.Close,
                    enabled: () => true,
                    url: '',
                    icon: CloseSidebarIcon,
                },
            ],
        },
        main: {
            title: 'sidebar.general.title',
            items: [
                {
                    title: 'sidebar.general.items.dashboard',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '/superAdmin/dashboard',
                    icon: DashboardSquareIcon,
                    activeIcon: DashboardSquareIconSolid,
                },
                {
                    title: 'sidebar.general.items.tenants',
                    type: SidebarButtons.Tenants,
                    enabled: () => true,
                    url: '/superAdmin/tenants',
                    icon: AnalyticsIcon,
                    activeIcon: Analytics_01_IconSolid,
                },
                {
                    title: 'sidebar.general.items.users',
                    type: SidebarButtons.Users,
                    enabled: () => true,
                    url: '/superAdmin/users',
                    icon: OwnersIcon,
                    activeIcon: OwnersIconSolid,
                },
                {
                    title: 'sidebar.general.items.userActivities',
                    type: SidebarButtons.UserActivities,
                    enabled: () => true,
                    url: '/superAdmin/user-activities',
                    icon: NotificationIcon,
                    activeIcon: NotificationIconSolid,
                },
                {
                    title: 'sidebar.general.items.supportRequests',
                    type: SidebarButtons.SupportEmailRequests,
                    enabled: () => true,
                    url: '/superAdmin/support-requests',
                    icon: Mail_02_Icon,
                    activeIcon: Mail_02_IconSolid,
                },
                {
                    title: 'sidebar.general.items.releases',
                    type: SidebarButtons.Releases,
                    enabled: () => true,
                    url: '/superAdmin/releases',
                    icon: QuestionsIcon,
                    activeIcon: QuestionsIconSolid,
                },
            ],
        },
        data: {
            title: 'sidebar.data.title',
            items: [
                {
                    title: 'sidebar.data.items.data',
                    type: SidebarButtons.Data,
                    enabled: () => true,
                    url: '/superAdmin/data',
                    icon: AnalyticsIcon,
                    activeIcon: Analytics_01_IconSolid,
                },
            ],
        },
        footer: {
            title: 'Footer',
            items: [
                {
                    title: 'sidebar.footer.items.logo',
                    type: SidebarButtons.Dashboard,
                    enabled: () => true,
                    url: '',
                    icon: OwnInnIcon,
                },
            ],
        },
    },
};
